import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AlertTriangle,
  FolderOpen,
  Loader2,
  PanelLeft,
  Search,
  SquarePen,
  Trash2,
  Share2,
} from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  AssistantActivity,
  AssistantState,
} from '@/embedded/chat/types/runtime-state';
import type {
  ConversationMessage,
  ConversationMessageContent,
} from '@/embedded/chat/types/conversation-message';
import {
  ConversationThread,
  useConversationMessageGroups,
} from '@/embedded/chat/components/messages/conversation-thread';
import { AssistantActivityLine } from '@/embedded/chat/components/messages/assistant-activity-line';
import { InteractiveLoginCompleterProvider } from '@/embedded/chat/components/messages/interactive-login-card';
import type { ConversationMessageGroup } from '@/embedded/chat/components/messages/assistant-turn';
import { createRandomID } from '@/utils/random-id';
import {
  getContentBoxMinimapInsights,
  MinimapOptionsProvider,
} from '@/embedded/chat/components/minimap';
import {
  stripLeadingSystemReminderBlock,
  stripTrailingSuggestionBlock,
  stripUserReminderBlocks,
} from '@/embedded/chat/types/conversation-message';
import {
  locationCompletionFromResult,
  requestGeolocation,
} from '../lib/app-request';
import { AppLogo } from '@/assets/logos/app-logo';
import { AnimatedLogo } from '@/embedded/chat/components/animated-logo';
import { cn } from '@/utils/cn';
import { buildQuoteContent, serializeQuoteTag } from '@/utils/chat-quote';
import { focusQuoteInContainer } from '@/utils/chat-quote-dom';
import { QuoteSelectionBubble } from '@/utils/quote-selection-bubble';
import {
  buildAgentFileURL,
  cancelAgentSession,
  completeInteractiveLogin,
  downloadAgentFile,
  HTTPStatusError,
  isAgentDBMigratingError,
  listAgentFiles,
  searchAgentConversations,
  type WebAgentCapabilityRequest,
  type WebAgentFileEntry,
  type WebAgentMessageContent,
  type WebConversationListItem,
  uploadAgentFile,
} from '../lib/chat-api';
import { getChatClientInstanceID } from '../lib/client-instance';
import { buildWebRequestContext } from '../lib/request-context';
import {
  getWebsiteBillingSummary,
  logoutWebsiteUser,
  type AuthUser,
  type WebsiteCredits,
} from '../lib/auth';
import { useWebsiteTitlebar } from '../lib/titlebar';
import {
  getShareByConversation,
  revokeSharedChat,
  shareChat as shareChatAPI,
  type SharedChatInfo,
} from '../lib/shared-chat-api';
import {
  chatConversationPath,
  resetWebsiteConversationDomainModelForTests,
  useRoutedConversationDomainModel,
} from '../lib/conversation-core';
import {
  ChatComposer,
  type ChatComposerAttachment,
} from '../components/chat/chat-composer';
import { PaymentRequiredBanner } from '../components/chat/payment-required-banner';
import { ChatSidebar } from '../components/chat/chat-sidebar';
import { chatLucideIconProps } from '../components/chat/chat-ui';
import { VFSBrowser } from '../components/chat/vfs-browser';
import {
  ChatFilePreviewModal,
  inferWebsiteFilePreviewKind,
  type WebsiteFilePreviewItem,
} from '../components/chat/file-preview-modal';
import { ChatScrollViewport } from '../components/chat/chat-scroll-viewport';
import {
  ConversationSearchPanel,
  type WebsiteConversationSearchResult,
} from '../components/chat/conversation-search-panel';
import { SlackLogo } from '../components/slack-logo';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

type FinishState = 'completed' | 'waiting' | 'failed' | 'cancelled' | null;

const CONVERSATION_SIDEBAR_AUTO_LOAD_MORE_THRESHOLD_PX = 160;
const CLIENT_DOWNLOAD_URL = 'https://bridge.surf/download';

type ComposerAttachmentState = ChatComposerAttachment & {
  file: File;
  uploadedPath?: string;
  uploadedSessionId?: string;
};

type DraftQuoteState = {
  text: string;
  quoteRef: NonNullable<ConversationMessageContent['quoteRef']>;
};

type BridgeComposerConnectorPath = {
  width: number;
  height: number;
  path: string;
};

const WEBSITE_CHAT_CONTENT_BOX_ID = 'website-chat-content-box';

type PendingConversationFocusRequest = {
  conversationId: string;
  messageId: string;
  requestId: number;
};

export function resetWebsiteChatPageStateForTests() {
  resetWebsiteConversationDomainModelForTests();
}

function normalizedText(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

function isActiveAssistantActivity(activity: AssistantActivity | null) {
  if (!activity) {
    return false;
  }
  const phase = normalizedText(activity.phase)?.toLowerCase();
  if (!phase || phase === 'idle') {
    return false;
  }
  const status = normalizedText(activity.status)?.toLowerCase();
  if (
    status === 'failed' ||
    status === 'error' ||
    status === 'cancelled' ||
    status === 'canceled' ||
    status === 'completed' ||
    status === 'done' ||
    status === 'success' ||
    status === 'paused'
  ) {
    return false;
  }
  return true;
}

function participantActivityAgentName(item: {
  participant: { agentName?: string | null; agentId?: string | null };
}) {
  return (
    normalizedText(item.participant.agentName) ??
    normalizedText(item.participant.agentId)
  );
}

function resolveThreadErrorState(error: unknown, fallback: string) {
  if (isAgentDBMigratingError(error)) {
    return {
      message: 'Conversation data is updating. Retrying shortly.',
      status: 503,
    };
  }
  if (error instanceof HTTPStatusError) {
    return {
      message: error.message || fallback,
      status: error.status,
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message || fallback,
      status: null,
    };
  }

  return {
    message: fallback,
    status: null,
  };
}

function capabilityPayloadString(
  payload: Record<string, unknown> | undefined,
  key: string
) {
  const value = payload?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

function isPaymentRequiredThreadError(message: string, status: number | null) {
  if (status !== null) {
    return status === 402;
  }

  return /\b402\b|payment required|out of credits|insufficient credits|more credits/i.test(
    message
  );
}

function lastNonEmptyPathSegment(path: string) {
  const segments = path.split('/');
  for (let index = segments.length - 1; index >= 0; index -= 1) {
    const segment = segments[index]?.trim();
    if (segment) {
      return segment;
    }
  }
  return '';
}

function joinAgentFilePath(dirPath: string, fileName: string): string {
  const sanitizedName = lastNonEmptyPathSegment(fileName) || fileName;
  if (dirPath === '/') {
    return `/${sanitizedName}`;
  }
  return `${dirPath}/${sanitizedName}`;
}

function basename(filePath: string): string {
  return lastNonEmptyPathSegment(filePath) || filePath;
}

function getGreetingLabel(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) {
    return 'Morning';
  }
  if (hour < 18) {
    return 'Afternoon';
  }
  return 'Evening';
}

function getUserGreetingName(user: AuthUser | null) {
  const displayName = user?.display_name?.trim();
  if (displayName) {
    return displayName;
  }

  const email = user?.email?.trim();
  if (!email) {
    return '';
  }

  return email.split('@')[0] || email;
}

function stripEmptyPlaceholder(text: string) {
  return text.replace(/<empty\s*\/>/g, '').trim();
}

type NormalizedMessageSignaturePart =
  | {
      type: 'text';
      text: string;
    }
  | {
      type: string;
      path: string;
      url: string;
      fileName: string;
      mimeType: string;
      fileRefs: Array<{
        path: string;
        environmentId: string;
        agentId: string;
        signature: string;
      }>;
    };

function normalizeMessageContentSignature(message: ConversationMessage) {
  const textParts = (message.content ?? []).filter(
    content => content.type === 'text'
  );
  const lastTextIndex = textParts.length - 1;
  let textIndex = 0;
  const normalized = (
    message.content ?? []
  ).flatMap<NormalizedMessageSignaturePart>(content => {
    if (content.type === 'text') {
      const rawText = content.text ?? '';
      const isLastTextBlock = textIndex === lastTextIndex;
      textIndex += 1;
      const userStripped =
        message.role === 'user'
          ? isLastTextBlock
            ? stripTrailingSuggestionBlock(
                stripUserReminderBlocks(
                  stripLeadingSystemReminderBlock(rawText)
                )
              )
            : stripUserReminderBlocks(stripLeadingSystemReminderBlock(rawText))
          : rawText;
      const visibleText = stripEmptyPlaceholder(userStripped);
      return visibleText ? [{ type: 'text', text: visibleText }] : [];
    }

    const path = content.fileRef?.path?.trim() ?? '';
    const url = content.url?.trim() ?? '';
    const fileName = content.fileName?.trim() ?? '';
    const mimeType = content.mimeType?.trim() ?? '';
    const fileRefs = (content.fileRefs ?? [])
      .map(ref => ({
        path: ref.path?.trim() ?? '',
        environmentId: ref.environmentId?.trim() ?? '',
        agentId: ref.agentId?.trim() ?? '',
        signature: ref.signature?.trim() ?? '',
      }))
      .filter(ref => ref.path);
    if (!path && !url && !fileName && fileRefs.length === 0) {
      return [];
    }

    return [
      {
        type: content.type,
        path,
        url,
        fileName,
        mimeType,
        fileRefs,
      },
    ];
  });

  return JSON.stringify(normalized);
}

type AssistantConversationItem = Extract<
  ConversationMessageGroup,
  { type: 'assistant' }
>['items'][number];

function isVisibleConversationGroup(
  group: ConversationMessageGroup,
  currentAssistantStateSequence?: number
) {
  if (group.type === 'user') {
    return Boolean(normalizeMessageContentSignature(group.message));
  }

  return group.items.some((item: AssistantConversationItem) => {
    if (item.type === 'state') {
      if (item.state.phase !== 'thinking' && item.state.phase !== 'execution') {
        return false;
      }

      if (item.state.sequence === currentAssistantStateSequence) {
        return true;
      }

      if (item.state.phase === 'thinking') {
        return Boolean(item.state.reasoning?.text?.trim());
      }

      return (item.state.tools?.length ?? 0) > 0;
    }

    if (item.message.type !== 'message') {
      return true;
    }

    if (item.message.error?.trim()) {
      return true;
    }

    return Boolean(normalizeMessageContentSignature(item.message));
  });
}

function inferAttachmentKind(file: File): ComposerAttachmentState['kind'] {
  if (file.type.startsWith('image/')) {
    return 'image';
  }
  if (file.type.startsWith('audio/')) {
    return 'audio';
  }
  if (file.type.startsWith('video/')) {
    return 'video';
  }
  return 'file';
}

function sanitizeAttachmentName(name: string) {
  const sanitized = name
    .trim()
    .replace(/[\\/]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9._-]/g, '-')
    .replace(/-+/g, '-');
  return sanitized || 'attachment';
}

function createSessionAttachmentAgentPath(sessionId: string, fileName: string) {
  return `/.willow/user/sessions/${sessionId}/attachments/${createRandomID()}-${sanitizeAttachmentName(fileName)}`;
}

function buildUserMessageContent(params: {
  text: string;
  attachments: WebAgentMessageContent[];
  quote?: DraftQuoteState | null;
}): {
  localContent: ConversationMessage['content'];
  outboundContent: WebAgentMessageContent[];
} {
  const trimmedText = params.text.trim();
  const text = trimmedText || '<empty/>';

  const localContent: ConversationMessage['content'] = [...params.attachments];
  const outboundContent: WebAgentMessageContent[] = [...params.attachments];

  if (params.quote) {
    localContent.push(
      buildQuoteContent(params.quote.text, params.quote.quoteRef)
    );
    outboundContent.push({
      type: 'text',
      text: serializeQuoteTag(params.quote.text, params.quote.quoteRef),
    });
  }

  localContent.push({
    type: 'text',
    text,
  });
  outboundContent.push({
    type: 'text',
    text,
  });

  return {
    localContent,
    outboundContent,
  };
}

function ChatWelcomeState({ user }: { user: AuthUser | null }) {
  const greetingName = getUserGreetingName(user);
  const greetingLabel = getGreetingLabel();

  return (
    <div className="flex min-h-[420px] w-full flex-1 items-center justify-center">
      <div className="flex max-w-[520px] flex-col items-center gap-4 text-center">
        <AppLogo className="size-16 text-foreground/18" />
        <div>
          <h1 className="mb-1 text-[24px] font-medium tracking-[-0.02em] text-foreground">
            {greetingLabel}
            {greetingName ? `, ${greetingName}` : ''}
          </h1>
          <p className="text-sm text-muted-foreground">
            How can I help you today?
          </p>
        </div>
      </div>
    </div>
  );
}

function ChatLoadingState() {
  return (
    <div className="flex min-h-[420px] w-full flex-1 items-center justify-center">
      <div className="flex max-w-[520px] flex-col items-center gap-4 text-center">
        <div className="flex size-16 items-center justify-center">
          <div>
            <AnimatedLogo
              loop
              className="text-foreground/18 h-[49px] ml-[2px] mt-px"
              aria-label="Loading conversation"
            />
          </div>
        </div>

        <div aria-hidden="true">
          <h1 className="mb-1 text-[24px] font-medium tracking-[-0.02em] text-transparent">
            &nbsp;
          </h1>
          <p className="text-sm text-transparent">&nbsp;</p>
        </div>
      </div>
    </div>
  );
}

const TELEGRAM_LOGO_PATH =
  'M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z';
const WECHAT_LOGO_PATH =
  'M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z';
const IMESSAGE_LOGO_PATH =
  'M5.285 0A5.273 5.273 0 0 0 0 5.285v13.43A5.273 5.273 0 0 0 5.285 24h13.43A5.273 5.273 0 0 0 24 18.715V5.285A5.273 5.273 0 0 0 18.715 0ZM12 4.154a8.809 7.337 0 0 1 8.809 7.338A8.809 7.337 0 0 1 12 18.828a8.809 7.337 0 0 1-2.492-.303A8.656 7.337 0 0 1 5.93 19.93a9.929 7.337 0 0 0 1.54-2.155 8.809 7.337 0 0 1-4.279-6.283A8.809 7.337 0 0 1 12 4.154';
const CODEX_ICON_SRC =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAkCAYAAADhAJiYAAAATmVYSWZNTQAqAAAACAAEARoABQAAAAEAAAA+ARsABQAAAAEAAABGASgAAwAAAAEAAgAAAhMAAwAAAAEAAQAAAAAAAAAAAJAAAAABAAAAkAAAAAElr9YpAAACt0lEQVRYCdWXzWpUQRCFxx9QFM1GBV14caEoGHe6dPIA6kQlj5C9O10JLnSbjS+gjyAGwbW6DaIuFSOIGDAbMTEGf84Xp0LRt293z9yZQQ8curq6qrrmdnd1T6fzj2FXi3wOyveEeEjcFDfEiWOfZrwhLok/xd+OLyXfEkl0IpjRLMuiTyImf5ZNTzwtXhArsc1KyL2Oa1L9EGMJlOhW5ftQnBZbgyDrYsnEORuWeUHcLQ6N5/LMTTTo+KJiDpXU3BiSseT5UsVgmV6I5jyOluUr2lNs4FHtmdwPYaMnMaPRNqcpl0A4zulrLAkUvVSdoRjey9iEE5b0K8WMggrcFGBNY1aBT0oe5ZJSPKPgC6QS2u+87iRsm2I06anoNfDrw7spDHDXee2V/E4MbYbp91zcbZEsc8G4zVkuwxUJOZ+Sce4+2w4Wu3O2MPiTbY+/wuNCv1xiN4O4nQMDBL7knC8O4JdKiqfLFnb2269qX/XlVPNdg2+cQeXkNuI5OU+FAXhcpX4FY7edE3XrQ4FPLqaN104bG4sNZgZh+1ZjnC4DRTK0adOvJcREs4lJ/N45JTtOnU9gRf2ueDjCI9JdFimw3sdkSg77uIam478aWD5V34JZez+wiXUfRfzwpyhHcV5am8C3v6TnRIF50Y+Z/Fr62sbEoY+jaj+JZu9brq0t7DCh31Zq3wc66xKAgMdMEWm/Sfcxomee4+KeyBgH44zIctbAU+CL6LMfp8xTp1vLIlA8mFBCvBiuBnNHu9PS5i7atl/tmebguirGgizbTtrkf704C2fIX5TFMSTFlxkaJMWXGtXysWcGWqamzNlT/DugODYtQ07PaSrawLIrBiWhEnkDU9F7YurusySXZdcVJwIuZB5XvGcsAVqWeUmkAvMqKEZYqYsdI4ZT0nE9AKo1b6z/H38AJjijHMuz2AYAAAAASUVORK5CYII=';

function SimpleBrandIcon({
  className,
  color,
  path,
}: {
  className?: string;
  color: string;
  path: string;
}) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path d={path} fill={color} />
    </svg>
  );
}

function ImageLogo({ className, src }: { className?: string; src: string }) {
  return <img src={src} alt="" aria-hidden="true" className={className} />;
}

function BridgeChatOnboardingState() {
  const channels = [
    { label: 'Slack', icon: <SlackLogo className="size-4" /> },
    {
      label: 'Telegram',
      icon: (
        <SimpleBrandIcon
          className="size-4"
          color="#26A5E4"
          path={TELEGRAM_LOGO_PATH}
        />
      ),
    },
    {
      label: 'iMessage',
      icon: (
        <SimpleBrandIcon
          className="size-4"
          color="#34DA50"
          path={IMESSAGE_LOGO_PATH}
        />
      ),
    },
    {
      label: 'WeChat',
      icon: (
        <SimpleBrandIcon
          className="size-4"
          color="#07C160"
          path={WECHAT_LOGO_PATH}
        />
      ),
    },
  ];
  const workers = [
    {
      title: 'OpenAI Worker',
      icon: (
        <ImageLogo className="size-8 dark:invert" src="/model-logos/gpt.svg" />
      ),
    },
    {
      title: 'Codex',
      icon: <ImageLogo className="size-8 dark:invert" src={CODEX_ICON_SRC} />,
    },
    {
      title: 'Claude Code',
      icon: <ImageLogo className="size-8" src="/model-logos/claude.svg" />,
    },
  ];

  return (
    <div className="flex min-h-[520px] w-full flex-1 justify-center px-5 pt-[5vh] md:px-8">
      <div className="relative flex w-full max-w-[920px] flex-1 flex-col">
        <div className="mx-auto mb-6 flex max-w-[620px] flex-col items-center gap-3 text-center">
          <h1 className="text-[44px] font-semibold leading-none tracking-normal text-foreground">
            Bridge
          </h1>
          <p className="text-[15px] leading-6 text-muted-foreground">
            <span className="block">
              One ongoing conversation across your apps.
            </span>
            <span className="block">
              Bridge keeps the thread and opens focused task chats when work
              needs depth.
            </span>
          </p>
        </div>

        <div className="relative mx-auto grid min-h-[340px] w-full grid-cols-1 items-center gap-8 md:grid-cols-[minmax(0,1fr)_168px_minmax(0,1fr)] md:gap-10">
          <svg
            className="pointer-events-none absolute inset-0 hidden h-full w-full md:block"
            viewBox="0 0 920 340"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient
                id="bridge-onboarding-flow"
                x1="0"
                y1="0"
                x2="1"
                y2="0"
              >
                <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
                <stop offset="46%" stopColor="currentColor" stopOpacity="0.3" />
                <stop offset="54%" stopColor="currentColor" stopOpacity="0.3" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </linearGradient>
            </defs>
            <g className="text-foreground/34" stroke="currentColor" fill="none">
              <path d="M238 92 C306 92 354 134 408 160" />
              <path d="M238 144 C306 144 354 154 408 166" />
              <path d="M238 196 C306 196 354 186 408 174" />
              <path d="M238 248 C306 248 354 206 408 180" />
            </g>
            <g
              className="text-foreground/42"
              stroke="currentColor"
              strokeWidth="1.15"
              fill="none"
            >
              <path d="M512 160 C566 120 608 112 682 112" />
              <path d="M512 170 C568 170 612 170 682 170" />
              <path d="M512 180 C566 220 608 228 682 228" />
            </g>
          </svg>

          <div className="relative z-10 flex w-[172px] flex-col gap-3 justify-self-center md:w-full md:max-w-[172px]">
            {channels.map(channel => (
              <div
                key={channel.label}
                className="flex h-10 items-center gap-2 rounded-full border border-border/70 bg-background px-3 text-[13px] font-medium text-foreground shadow-sm"
              >
                <span className="flex size-5 shrink-0 items-center justify-center">
                  {channel.icon}
                </span>
                <span className="truncate">{channel.label}</span>
              </div>
            ))}
          </div>

          <div className="relative z-10 flex justify-center">
            <div
              className="relative flex size-[116px] items-center justify-center rounded-[30px] border border-border/80 bg-background shadow-[0_22px_80px_rgba(0,0,0,0.14)]"
              data-bridge-onboarding-anchor="true"
            >
              <div className="absolute inset-[-14px] rounded-[40px] border border-border/45" />
              <div className="absolute inset-[-28px] rounded-[52px] border border-border/25" />
              <AppLogo className="size-14 text-foreground" />
            </div>
          </div>

          <div className="relative z-10 flex flex-col gap-2.5 justify-self-center md:w-full">
            {workers.map(worker => (
              <div
                key={worker.title}
                className="flex h-12 items-center gap-3 rounded-full bg-background/90 px-2 text-foreground"
              >
                <div className="flex size-9 shrink-0 items-center justify-center">
                  {worker.icon}
                </div>
                <div className="min-w-0 truncate text-[14px] font-medium">
                  {worker.title}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="min-h-2 flex-1" aria-hidden="true" />
      </div>
    </div>
  );
}

export function ChatPage(): React.JSX.Element {
  const { conversationId } = useParams();
  const selectedRouteId = normalizedText(conversationId);
  const [searchParams] = useSearchParams();
  const routeAgentGroupId = normalizedText(searchParams.get('agentGroupId'));
  const navigate = useNavigate();
  const conversationModel = useRoutedConversationDomainModel(
    selectedRouteId,
    routeAgentGroupId
  );
  const isMountedRef = useRef(false);
  const loginPathRef = useRef('/login');
  const conversationListLoadMoreInFlightRef = useRef(false);
  const selectedConversationIdRef = useRef<string | null>(null);
  const selectedShareConversationIdRef = useRef<string | null>(null);
  const createConversationPromiseRef = useRef<Promise<
    WebConversationListItem | undefined
  > | null>(null);
  const composerAttachmentsRef = useRef<ComposerAttachmentState[]>([]);
  const submitInFlightRef = useRef(false);
  const chatSurfaceRef = useRef<HTMLDivElement | null>(null);

  const [loadingMoreConversations, setLoadingMoreConversations] =
    useState(false);
  const [clientInstanceId] = useState(getChatClientInstanceID);
  const handledCapabilityRequestIdsRef = useRef<Set<string>>(new Set());
  const [finishState, setFinishState] = useState<FinishState>(null);
  const [threadError, setThreadError] = useState('');
  const [threadErrorStatus, setThreadErrorStatus] = useState<number | null>(
    null
  );
  const [streamNotice, setStreamNotice] = useState('');
  const [composerValue, setComposerValue] = useState('');
  const [composerQuote, setComposerQuote] = useState<DraftQuoteState | null>(
    null
  );
  const [composerAttachments, setComposerAttachments] = useState<
    ComposerAttachmentState[]
  >([]);
  const [composerFocusRequestKey, setComposerFocusRequestKey] = useState(0);
  const [bridgeComposerConnectorPath, setBridgeComposerConnectorPath] =
    useState<BridgeComposerConnectorPath | null>(null);
  const [creatingConversation, setCreatingConversation] = useState(false);
  const [deletingConversationId, setDeletingConversationId] = useState<
    string | null
  >(null);
  const [renamingConversationId, setRenamingConversationId] = useState<
    string | null
  >(null);
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isVFSOpen, setIsVFSOpen] = useState(false);
  const [vfsPath, setVFSPath] = useState('/');
  const [vfsEntries, setVFSEntries] = useState<WebAgentFileEntry[]>([]);
  const [isLoadingVFS, setIsLoadingVFS] = useState(false);
  const [hasLoadedVFS, setHasLoadedVFS] = useState(false);
  const [vfsError, setVFSError] = useState('');
  const [isUploadingVFS, setIsUploadingVFS] = useState(false);
  const [downloadingVFSPath, setDownloadingVFSPath] = useState<string | null>(
    null
  );
  const [filePreview, setFilePreview] = useState<WebsiteFilePreviewItem | null>(
    null
  );
  const [shareState, setShareState] = useState<
    'idle' | 'loading' | 'sharing' | 'revoking' | 'copied'
  >('idle');
  const [shareInfo, setShareInfo] = useState<SharedChatInfo | null>(null);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [refreshedUserCredits, setRefreshedUserCredits] =
    useState<WebsiteCredits | null>(null);
  const agentGroupIdRef = useRef<string | null>(null);
  const [refreshingCredits, setRefreshingCredits] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchPanelRequestVersion, setSearchPanelRequestVersion] = useState(0);
  const [pendingFocusMessageRequest, setPendingFocusMessageRequest] =
    useState<PendingConversationFocusRequest | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  const handleDownloadClient = useCallback(() => {
    window.location.assign(CLIENT_DOWNLOAD_URL);
  }, []);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const focusedMessageElementRef = useRef<HTMLElement | null>(null);
  const focusedMessageCleanupTimeoutRef = useRef<number | null>(null);
  const focusMessageRequestIdRef = useRef(0);
  const minimapOptions = useMemo(
    () => ({
      getInsights: () =>
        getContentBoxMinimapInsights(WEBSITE_CHAT_CONTENT_BOX_ID),
    }),
    []
  );
  const assistantStateSequence = useMemo<AssistantState[]>(() => [], []);
  const { currentAssistantState, groupedMessages } =
    useConversationMessageGroups({
      messages: conversationModel.selectedMessages.value,
      assistantStateSequence,
    });
  useWebsiteTitlebar({ sourceRef: pageRef });

  const modelConversationListItems =
    conversationModel.historyConversationListItems.value;
  const modelConversationListHydrated =
    conversationModel.conversationListHydrated.value;
  const modelConversationListNextCursor =
    conversationModel.conversationListNextCursor.value;
  const modelHasMoreConversationList =
    conversationModel.hasMoreConversationList.value;
  const modelAgentGroupId = conversationModel.agentGroupId.value;
  const modelBootstrapLoading = conversationModel.bootstrapLoading.value;
  const modelBootstrapError = conversationModel.bootstrapError.value;
  const currentUser = conversationModel.currentUser.value as AuthUser | null;
  const currentUserCredits =
    refreshedUserCredits ?? conversationModel.currentUserCredits.value;
  const loadingCredits =
    refreshingCredits || (modelBootstrapLoading && currentUserCredits === null);
  const modelSelectedMessages = conversationModel.selectedMessages.value;
  const messages = modelSelectedMessages;
  const selectedMessageThread = conversationModel.selectedMessageThread.value;
  const modelParticipantActivities =
    conversationModel.selectedParticipantActivities.value;
  const modelIsProcessing = conversationModel.isProcessing.value;
  const modelSelectedRuntimeRef = conversationModel.selectedRuntimeRef.value;
  const modelIsSubmittingMessage = conversationModel.isSubmittingMessage.value;
  const modelSelectedListDeleted = conversationModel.selectedListDeleted.value;
  const modelPendingCapabilityRequests =
    conversationModel.pendingCapabilityRequests.value;
  const isBridgeChatVirtualOnboardingSelected =
    conversationModel.isBridgeChatVirtualOnboardingSelected.value;
  const isBridgeChatSelected = conversationModel.isBridgeChatSelected.value;
  const isBridgeChatOnboardingSelected =
    conversationModel.isBridgeChatOnboardingSelected.value;

  const conversationsById = conversationModel.conversationListItemsById.value;
  const conversations = useMemo(
    () =>
      deletingConversationId
        ? modelConversationListItems.filter(
            conversation =>
              conversation.conversationId !== deletingConversationId
          )
        : modelConversationListItems,
    [deletingConversationId, modelConversationListItems]
  );
  const conversationListNextCursor = modelConversationListNextCursor;
  const hasMoreConversationList = modelHasMoreConversationList;
  const selectedRouteInfoPending =
    conversationModel.selectedRouteInfoPending.value;
  const selectedConversationId = conversationModel.selectedConversationId.value;
  const selectedListConversationId =
    conversationModel.selectedListConversationId.value;
  const nextConversationId = conversationModel.nextConversationId.value;
  const selectedConversationListItem =
    conversationModel.selectedConversationListItem.value;
  selectedConversationIdRef.current = selectedConversationId;
  const fallbackRouteConversationId =
    conversationModel.fallbackRouteConversationId.value;
  const selectedRuntimeSessionId = modelSelectedRuntimeRef?.sessionId ?? null;
  const selectedAgentGroupId =
    conversationModel.selectedAgentGroupIdForCommands.value;
  agentGroupIdRef.current = selectedAgentGroupId;
  const loadingConversations =
    !modelConversationListHydrated &&
    (modelBootstrapLoading || Boolean(modelAgentGroupId));
  const displayedParticipantActivities = modelParticipantActivities;
  const hasActiveAssistantActivity = displayedParticipantActivities.some(item =>
    isActiveAssistantActivity(item.activity)
  );
  const isConversationProcessing =
    modelIsProcessing || hasActiveAssistantActivity;
  const hasRenderableConversation =
    groupedMessages.some(group =>
      isVisibleConversationGroup(group, currentAssistantState?.sequence)
    ) || hasActiveAssistantActivity;
  const isSelectedConversationReadOnly = selectedRouteInfoPending
    ? true
    : conversationModel.isSelectedConversationReadOnly.value;
  const showSelectedConversationControls =
    !isSelectedConversationReadOnly && !isBridgeChatOnboardingSelected;
  const selectedConversationTitle =
    conversationModel.selectedConversationTitle.value;
  const showPaymentRequiredBanner =
    threadError.length > 0 &&
    isPaymentRequiredThreadError(threadError, threadErrorStatus);
  const canSubmitComposer =
    (composerValue.trim().length > 0 || composerAttachments.length > 0) &&
    !modelIsSubmittingMessage &&
    conversationModel.canSubmitSelectedConversation.value;
  const shareURL = shareInfo?.public_url ?? null;
  const selectedShareConversationId =
    conversationModel.selectedShareConversationId.value;
  selectedShareConversationIdRef.current = selectedShareConversationId;
  const showConversationLoading =
    conversationModel.selectedConversationLoading.value ||
    (!selectedConversationId && creatingConversation);
  const chatBottomInset = 0;

  useLayoutEffect(() => {
    if (!isBridgeChatOnboardingSelected) {
      setBridgeComposerConnectorPath(null);
      return;
    }

    const surface = chatSurfaceRef.current;
    if (!surface) {
      setBridgeComposerConnectorPath(null);
      return;
    }

    let animationFrameID = 0;

    const updateConnectorPath = () => {
      animationFrameID = 0;

      const bridgeAnchor = surface.querySelector<HTMLElement>(
        '[data-bridge-onboarding-anchor="true"]'
      );
      const composerAnchor = surface.querySelector<HTMLElement>(
        '[data-chat-composer-anchor="true"]'
      );
      if (!bridgeAnchor || !composerAnchor) {
        setBridgeComposerConnectorPath(null);
        return;
      }

      const surfaceRect = surface.getBoundingClientRect();
      const bridgeRect = bridgeAnchor.getBoundingClientRect();
      const composerRect = composerAnchor.getBoundingClientRect();
      if (surfaceRect.width <= 0 || surfaceRect.height <= 0) {
        setBridgeComposerConnectorPath(null);
        return;
      }

      const startX = bridgeRect.left + bridgeRect.width / 2 - surfaceRect.left;
      const startY = bridgeRect.bottom - surfaceRect.top;
      const endX =
        composerRect.left + composerRect.width / 2 - surfaceRect.left;
      const endY = composerRect.top - surfaceRect.top + 2;
      const gap = endY - startY;
      if (gap < 24) {
        setBridgeComposerConnectorPath(null);
        return;
      }

      const controlOffset = Math.max(48, gap * 0.36);
      const nextPath = [
        `M ${startX.toFixed(1)} ${startY.toFixed(1)}`,
        `C ${startX.toFixed(1)} ${(startY + controlOffset).toFixed(1)}`,
        `${endX.toFixed(1)} ${(endY - controlOffset).toFixed(1)}`,
        `${endX.toFixed(1)} ${endY.toFixed(1)}`,
      ].join(' ');
      const nextConnector = {
        width: Math.round(surfaceRect.width),
        height: Math.round(surfaceRect.height),
        path: nextPath,
      };

      setBridgeComposerConnectorPath(current => {
        if (
          current?.width === nextConnector.width &&
          current.height === nextConnector.height &&
          current.path === nextConnector.path
        ) {
          return current;
        }
        return nextConnector;
      });
    };

    const scheduleConnectorUpdate = () => {
      if (animationFrameID) return;
      animationFrameID = window.requestAnimationFrame(updateConnectorPath);
    };

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(scheduleConnectorUpdate);
    resizeObserver?.observe(surface);

    const mutationObserver =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver(scheduleConnectorUpdate);
    mutationObserver?.observe(surface, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    window.addEventListener('resize', scheduleConnectorUpdate);
    scheduleConnectorUpdate();

    return () => {
      if (animationFrameID) {
        window.cancelAnimationFrame(animationFrameID);
      }
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', scheduleConnectorUpdate);
    };
  }, [isBridgeChatOnboardingSelected]);

  const conversationTitlesById = useMemo(() => {
    return new Map(
      [...conversationsById.values()].map(conversation => {
        return [conversation.conversationId, conversation.title] as const;
      })
    );
  }, [conversationsById]);
  const toggleDesktopSidebar = useCallback(() => {
    setIsDesktopSidebarOpen(current => !current);
  }, []);

  const toggleVFS = useCallback(() => {
    setIsVFSOpen(current => !current);
  }, []);

  const closeVFS = useCallback(() => {
    setIsVFSOpen(false);
  }, []);

  const openMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(true);
  }, []);

  const closeMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(false);
  }, []);

  const openSearchPanel = useCallback(() => {
    setSearchPanelRequestVersion(version => version + 1);
    setIsSearchOpen(true);
  }, []);

  const closeSearchPanel = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  const redirectToLogin = useCallback(() => {
    navigate(loginPathRef.current, { replace: true });
  }, [navigate]);

  const isUnauthorizedError = useCallback((error: unknown) => {
    return (
      (error instanceof HTTPStatusError && error.status === 401) ||
      (typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        error.status === 401)
    );
  }, []);

  const requestComposerFocus = useCallback(() => {
    setComposerFocusRequestKey(key => key + 1);
  }, []);

  useEffect(() => {
    if (isSelectedConversationReadOnly) {
      setIsVFSOpen(false);
    }
  }, [isSelectedConversationReadOnly]);

  const approveLocationCapabilityRequest = useCallback(
    async (requestId: string) => {
      const currentAgentGroupId = agentGroupIdRef.current;
      const normalizedRequestId = requestId.trim();
      if (
        !currentAgentGroupId ||
        !normalizedRequestId ||
        handledCapabilityRequestIdsRef.current.has(normalizedRequestId)
      ) {
        return;
      }

      handledCapabilityRequestIdsRef.current.add(normalizedRequestId);
      try {
        const location = await requestGeolocation();
        await conversationModel.shareLocationCapabilityRequest(
          normalizedRequestId,
          locationCompletionFromResult(location)
        );
      } catch (error) {
        handledCapabilityRequestIdsRef.current.delete(normalizedRequestId);
        if (isUnauthorizedError(error)) {
          redirectToLogin();
          return;
        }
        const nextError = resolveThreadErrorState(
          error,
          'Failed to share location'
        );
        setThreadError(nextError.message);
        setThreadErrorStatus(nextError.status);
      }
    },
    [conversationModel, isUnauthorizedError, redirectToLogin]
  );

  const runCapabilityRequestAction = useCallback(
    async (
      requestId: string,
      noAgentGroupMessage: string,
      failureMessage: string,
      action: (normalizedRequestId: string) => Promise<unknown>
    ) => {
      const currentAgentGroupId = agentGroupIdRef.current;
      const normalizedRequestId = requestId.trim();
      if (!currentAgentGroupId) {
        throw new Error(noAgentGroupMessage);
      }
      if (!normalizedRequestId) {
        throw new Error('Capability request id is required.');
      }
      if (handledCapabilityRequestIdsRef.current.has(normalizedRequestId)) {
        throw new Error('Capability request is already being completed.');
      }

      handledCapabilityRequestIdsRef.current.add(normalizedRequestId);
      try {
        await action(normalizedRequestId);
      } catch (error) {
        handledCapabilityRequestIdsRef.current.delete(normalizedRequestId);
        if (isUnauthorizedError(error)) {
          redirectToLogin();
          throw error;
        }
        const nextError = resolveThreadErrorState(error, failureMessage);
        setThreadError(nextError.message);
        setThreadErrorStatus(nextError.status);
        throw error;
      }
    },
    [isUnauthorizedError, redirectToLogin]
  );

  const confirmOAuthAuthorizationCapabilityRequest = useCallback(
    (requestId: string) =>
      runCapabilityRequestAction(
        requestId,
        'No active agent group for OAuth authorization request.',
        'Failed to confirm OAuth authorization',
        normalizedRequestId =>
          conversationModel.confirmOAuthAuthorizationCapabilityRequest(
            normalizedRequestId
          )
      ),
    [conversationModel, runCapabilityRequestAction]
  );

  const completeInteractiveLoginCapabilityRequest = useCallback(
    (requestId: string) =>
      runCapabilityRequestAction(
        requestId,
        'No active agent group for interactive login request.',
        'Failed to complete interactive login request',
        normalizedRequestId =>
          conversationModel.completeInteractiveLoginCapabilityRequest(
            normalizedRequestId
          )
      ),
    [conversationModel, runCapabilityRequestAction]
  );
  const decideHostAccessCapabilityRequest = useCallback(
    async (requestId: string, decision: { approved: boolean }) => {
      const currentAgentGroupId = agentGroupIdRef.current;
      const normalizedRequestId = requestId.trim();
      if (!currentAgentGroupId) {
        throw new Error('No active agent group for host access request.');
      }
      if (!normalizedRequestId) {
        throw new Error('Capability request id is required.');
      }
      if (handledCapabilityRequestIdsRef.current.has(normalizedRequestId)) {
        throw new Error('Capability request is already being completed.');
      }

      handledCapabilityRequestIdsRef.current.add(normalizedRequestId);
      try {
        await conversationModel.decideHostAccessCapabilityRequest(
          normalizedRequestId,
          decision
        );
      } catch (error) {
        handledCapabilityRequestIdsRef.current.delete(normalizedRequestId);
        if (isUnauthorizedError(error)) {
          redirectToLogin();
          throw error;
        }
        const nextError = resolveThreadErrorState(
          error,
          'Failed to decide host access request'
        );
        setThreadError(nextError.message);
        setThreadErrorStatus(nextError.status);
        throw error;
      }
    },
    [conversationModel, isUnauthorizedError, redirectToLogin]
  );
  const decideComputerUseStartCapabilityRequest = useCallback(
    async (
      requestId: string,
      decision: { approved: boolean; mode?: string }
    ) => {
      const currentAgentGroupId = agentGroupIdRef.current;
      const normalizedRequestId = requestId.trim();
      if (!currentAgentGroupId) {
        throw new Error('No active agent group for ComputerUse request.');
      }
      if (!normalizedRequestId) {
        throw new Error('Capability request id is required.');
      }
      if (handledCapabilityRequestIdsRef.current.has(normalizedRequestId)) {
        throw new Error('Capability request is already being completed.');
      }

      handledCapabilityRequestIdsRef.current.add(normalizedRequestId);
      try {
        await conversationModel.decideComputerUseStartCapabilityRequest(
          normalizedRequestId,
          decision
        );
      } catch (error) {
        handledCapabilityRequestIdsRef.current.delete(normalizedRequestId);
        if (isUnauthorizedError(error)) {
          redirectToLogin();
          throw error;
        }
        const nextError = resolveThreadErrorState(
          error,
          'Failed to decide ComputerUse request'
        );
        setThreadError(nextError.message);
        setThreadErrorStatus(nextError.status);
        throw error;
      }
    },
    [conversationModel, isUnauthorizedError, redirectToLogin]
  );

  const handleCapabilityRequest = useCallback(
    async (request: WebAgentCapabilityRequest) => {
      const currentAgentGroupId = agentGroupIdRef.current;
      if (
        request.status !== 'pending' ||
        request.requestType !== 'location' ||
        !currentAgentGroupId ||
        handledCapabilityRequestIdsRef.current.has(request.requestId)
      ) {
        return;
      }

      const triggerClientInstanceId = capabilityPayloadString(
        request.requestPayload,
        'trigger_client_instance_id'
      );
      if (
        !triggerClientInstanceId ||
        triggerClientInstanceId !== clientInstanceId
      ) {
        return;
      }

      await approveLocationCapabilityRequest(request.requestId);
    },
    [approveLocationCapabilityRequest, clientInstanceId]
  );
  useEffect(() => {
    for (const request of modelPendingCapabilityRequests) {
      void handleCapabilityRequest(request);
    }
  }, [handleCapabilityRequest, modelPendingCapabilityRequests]);

  const loadMoreConversations = useCallback(async () => {
    if (
      !selectedAgentGroupId ||
      !conversationListNextCursor ||
      loadingMoreConversations ||
      conversationListLoadMoreInFlightRef.current
    ) {
      return;
    }

    conversationListLoadMoreInFlightRef.current = true;
    setLoadingMoreConversations(true);
    try {
      await conversationModel.refreshConversationList({
        limit: 50,
        cursor: conversationListNextCursor,
        restartStream: false,
      });
      if (!isMountedRef.current) {
        return;
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      if (isUnauthorizedError(error)) {
        redirectToLogin();
      }
    } finally {
      conversationListLoadMoreInFlightRef.current = false;
      if (isMountedRef.current) {
        setLoadingMoreConversations(false);
      }
    }
  }, [
    conversationModel,
    isUnauthorizedError,
    loadingMoreConversations,
    redirectToLogin,
    conversationListLoadMoreInFlightRef,
    conversationListNextCursor,
    selectedAgentGroupId,
  ]);

  const loadVFS = useCallback(
    async (targetPath = vfsPath) => {
      setIsLoadingVFS(true);
      setVFSError('');
      try {
        const entries = await listAgentFiles(targetPath);
        if (!isMountedRef.current) {
          return;
        }
        setVFSEntries(entries);
        setVFSPath(targetPath);
        setHasLoadedVFS(true);
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        if (isUnauthorizedError(error)) {
          redirectToLogin();
          return;
        }
        setVFSError(
          error instanceof Error ? error.message : 'Failed to load VFS files'
        );
      } finally {
        if (isMountedRef.current) {
          setIsLoadingVFS(false);
        }
      }
    },
    [isUnauthorizedError, redirectToLogin, vfsPath]
  );

  const handleDownloadVFSFile = useCallback(
    async (filePath: string) => {
      setDownloadingVFSPath(filePath);
      setVFSError('');
      try {
        const blob = await downloadAgentFile(filePath);
        const objectURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectURL;
        link.download = basename(filePath);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => {
          window.URL.revokeObjectURL(objectURL);
        }, 1000);
      } catch (error) {
        if (isUnauthorizedError(error)) {
          redirectToLogin();
          return;
        }
        setVFSError(
          error instanceof Error ? error.message : 'Failed to download file'
        );
      } finally {
        if (isMountedRef.current) {
          setDownloadingVFSPath(current =>
            current === filePath ? null : current
          );
        }
      }
    },
    [isUnauthorizedError, redirectToLogin]
  );

  const handlePreviewVFSFile = useCallback((filePath: string) => {
    const fileName = basename(filePath);
    setFilePreview({
      title: fileName,
      url: buildAgentFileURL(filePath),
      fileName,
      filePath,
      kind: inferWebsiteFilePreviewKind({ fileName, filePath }),
    });
  }, []);

  const handleUploadVFSFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) {
        return;
      }

      setIsUploadingVFS(true);
      setVFSError('');
      try {
        await Promise.all(
          files.map(file =>
            uploadAgentFile(joinAgentFilePath(vfsPath, file.name), file)
          )
        );
        await loadVFS(vfsPath);
      } catch (error) {
        if (isUnauthorizedError(error)) {
          redirectToLogin();
          return;
        }
        setVFSError(
          error instanceof Error ? error.message : 'Failed to upload files'
        );
      } finally {
        if (isMountedRef.current) {
          setIsUploadingVFS(false);
        }
      }
    },
    [isUnauthorizedError, loadVFS, redirectToLogin, vfsPath]
  );

  const revokeComposerAttachmentPreview = useCallback(
    (attachment: ComposerAttachmentState) => {
      if (attachment.previewUrl?.startsWith('blob:')) {
        window.URL.revokeObjectURL(attachment.previewUrl);
      }
    },
    []
  );

  const clearComposerAttachments = useCallback(
    (attachmentsToClear?: ComposerAttachmentState[]) => {
      const targetAttachments =
        attachmentsToClear ?? composerAttachmentsRef.current;
      for (const attachment of targetAttachments) {
        revokeComposerAttachmentPreview(attachment);
      }
      setComposerAttachments([]);
    },
    [revokeComposerAttachmentPreview]
  );

  const updateComposerAttachment = useCallback(
    (
      attachmentId: string,
      updater: (attachment: ComposerAttachmentState) => ComposerAttachmentState
    ) => {
      setComposerAttachments(current =>
        current.map(attachment =>
          attachment.id === attachmentId ? updater(attachment) : attachment
        )
      );
    },
    []
  );

  const handlePickComposerFiles = useCallback((files: File[]) => {
    if (files.length === 0) {
      return;
    }

    setComposerAttachments(current => [
      ...current,
      ...files.map(file => ({
        id: createRandomID(),
        file,
        kind: inferAttachmentKind(file),
        name: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
        previewUrl: file.type.startsWith('image/')
          ? window.URL.createObjectURL(file)
          : undefined,
        status: 'idle' as const,
      })),
    ]);
  }, []);

  const handleRemoveComposerAttachment = useCallback(
    (attachmentId: string) => {
      setComposerAttachments(current => {
        const target = current.find(
          attachment => attachment.id === attachmentId
        );
        if (target) {
          revokeComposerAttachmentPreview(target);
        }
        return current.filter(attachment => attachment.id !== attachmentId);
      });
    },
    [revokeComposerAttachmentPreview]
  );

  const prepareComposerAttachment = useCallback(
    async (
      attachment: ComposerAttachmentState,
      targetSessionId: string
    ): Promise<WebAgentMessageContent> => {
      updateComposerAttachment(attachment.id, current => ({
        ...current,
        status: 'uploading',
        error: undefined,
      }));

      try {
        const reusableUploadedPath =
          attachment.uploadedPath &&
          attachment.uploadedSessionId === targetSessionId
            ? attachment.uploadedPath
            : undefined;
        const uploadedPath =
          reusableUploadedPath ??
          createSessionAttachmentAgentPath(targetSessionId, attachment.name);
        if (!reusableUploadedPath) {
          const result = await uploadAgentFile(uploadedPath, attachment.file);
          updateComposerAttachment(attachment.id, current => ({
            ...current,
            uploadedPath: result.path,
            uploadedSessionId: targetSessionId,
            status: 'ready',
            error: undefined,
          }));

          return {
            type: attachment.kind,
            url:
              attachment.kind === 'image'
                ? buildAgentFileURL(result.path)
                : undefined,
            fileRef: {
              environmentId: 'vfs',
              path: result.path,
            },
            fileRefs: [
              {
                environmentId: 'vfs',
                path: result.path,
              },
            ],
            fileName: attachment.name,
            mimeType: attachment.mimeType,
            sizeBytes: attachment.size,
            entryKind: 'file',
          };
        }

        updateComposerAttachment(attachment.id, current => ({
          ...current,
          uploadedPath,
          uploadedSessionId: targetSessionId,
          status: 'ready',
          error: undefined,
        }));

        return {
          type: attachment.kind,
          url:
            attachment.kind === 'image'
              ? buildAgentFileURL(uploadedPath)
              : undefined,
          fileRef: {
            environmentId: 'vfs',
            path: uploadedPath,
          },
          fileRefs: [
            {
              environmentId: 'vfs',
              path: uploadedPath,
            },
          ],
          fileName: attachment.name,
          mimeType: attachment.mimeType,
          sizeBytes: attachment.size,
          entryKind: 'file',
        };
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to prepare attachment';

        updateComposerAttachment(attachment.id, current => ({
          ...current,
          status: 'failed',
          error: errorMessage,
        }));
        throw error;
      }
    },
    [updateComposerAttachment]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      for (const attachment of composerAttachmentsRef.current) {
        revokeComposerAttachmentPreview(attachment);
      }
    };
  }, [revokeComposerAttachmentPreview]);

  useEffect(() => {
    const next = `${location.pathname}${location.search}`;
    loginPathRef.current = `/login?next=${encodeURIComponent(next)}`;
  }, []);

  useEffect(() => {
    if (modelBootstrapError && isUnauthorizedError(modelBootstrapError)) {
      redirectToLogin();
    }
  }, [isUnauthorizedError, modelBootstrapError, redirectToLogin]);

  const refreshCurrentUserCredits = useCallback(async () => {
    setRefreshingCredits(true);

    try {
      const credits = await getWebsiteBillingSummary();
      if (isMountedRef.current) {
        setRefreshedUserCredits(credits);
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      if (isUnauthorizedError(error)) {
        redirectToLogin();
      }
    } finally {
      if (isMountedRef.current) {
        setRefreshingCredits(false);
      }
    }
  }, [isUnauthorizedError, redirectToLogin]);

  useEffect(() => {
    composerAttachmentsRef.current = composerAttachments;
  }, [composerAttachments]);

  useEffect(() => {
    if (!selectedListConversationId || !modelSelectedListDeleted) {
      return;
    }

    setFinishState(null);
    setThreadError('');
    setThreadErrorStatus(null);
    navigate(
      nextConversationId ? chatConversationPath(nextConversationId) : '/chat',
      {
        replace: true,
      }
    );
  }, [
    modelSelectedListDeleted,
    navigate,
    nextConversationId,
    selectedListConversationId,
  ]);

  useEffect(() => {
    if (!isVFSOpen || hasLoadedVFS) {
      return;
    }

    void loadVFS(vfsPath);
  }, [hasLoadedVFS, isVFSOpen, loadVFS, vfsPath]);

  useEffect(() => {
    if (
      selectedConversationId ||
      loadingConversations ||
      creatingConversation ||
      createConversationPromiseRef.current ||
      !fallbackRouteConversationId
    ) {
      return;
    }

    navigate(chatConversationPath(fallbackRouteConversationId), {
      replace: true,
    });
  }, [
    creatingConversation,
    fallbackRouteConversationId,
    loadingConversations,
    navigate,
    selectedConversationId,
  ]);

  const clearFocusedMessage = useCallback(() => {
    if (focusedMessageCleanupTimeoutRef.current !== null) {
      window.clearTimeout(focusedMessageCleanupTimeoutRef.current);
      focusedMessageCleanupTimeoutRef.current = null;
    }

    focusedMessageElementRef.current?.classList.remove(
      'website-conversation-message-focused'
    );
    focusedMessageElementRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      clearFocusedMessage();
    };
  }, [clearFocusedMessage]);

  const findMessageElement = useCallback((messageId: string) => {
    const container = scrollContainerRef.current;
    if (!container) {
      return null;
    }

    const escapedId = window.CSS?.escape
      ? window.CSS.escape(messageId)
      : messageId.replace(/["\\]/g, '\\$&');

    return container.querySelector<HTMLElement>(
      `[data-message-id="${escapedId}"]`
    );
  }, []);

  const highlightMessageElement = useCallback(
    (element: HTMLElement) => {
      clearFocusedMessage();
      element.classList.remove('website-conversation-message-focused');
      void element.getBoundingClientRect();
      element.classList.add('website-conversation-message-focused');
      element.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
      focusedMessageElementRef.current = element;
      focusedMessageCleanupTimeoutRef.current = window.setTimeout(() => {
        if (focusedMessageElementRef.current !== element) {
          return;
        }
        element.classList.remove('website-conversation-message-focused');
        focusedMessageElementRef.current = null;
        focusedMessageCleanupTimeoutRef.current = null;
      }, 2200);
    },
    [clearFocusedMessage]
  );

  const focusQuote = useCallback((quote: DraftQuoteState['quoteRef']) => {
    const container = document.getElementById(WEBSITE_CHAT_CONTENT_BOX_ID);
    return focusQuoteInContainer({
      container: container as HTMLElement | null,
      quoteRef: quote,
      focusClassName: 'website-conversation-message-focused',
    });
  }, []);

  useEffect(() => {
    if (
      !pendingFocusMessageRequest ||
      pendingFocusMessageRequest.conversationId !== selectedConversationId
    ) {
      return;
    }

    let timeoutId: number | null = null;
    let cancelled = false;
    const request = pendingFocusMessageRequest;

    const clearPendingRequest = () => {
      setPendingFocusMessageRequest(current =>
        current?.requestId === request.requestId ? null : current
      );
    };

    const attemptFocus = (attempt: number) => {
      if (cancelled) {
        return;
      }

      const element = findMessageElement(request.messageId);
      if (element) {
        highlightMessageElement(element);
        clearPendingRequest();
        return;
      }

      if (attempt >= 20) {
        clearPendingRequest();
        return;
      }

      timeoutId = window.setTimeout(
        () => attemptFocus(attempt + 1),
        attempt < 6 ? 80 : 140
      );
    };

    const frame = window.requestAnimationFrame(() => {
      attemptFocus(0);
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    findMessageElement,
    groupedMessages.length,
    highlightMessageElement,
    messages.length,
    pendingFocusMessageRequest,
    selectedConversationId,
  ]);

  const handleConversationSearch = useCallback(
    async (query: string): Promise<WebsiteConversationSearchResult[]> => {
      const results = await searchAgentConversations(query);
      return results.flatMap(result => {
        const conversationId = result.conversationId.trim();
        if (!conversationId) {
          return [];
        }

        const conversationTitle =
          conversationTitlesById.get(conversationId)?.trim() ||
          result.title.trim() ||
          'Untitled';
        const messageId = result.messageId?.trim() ?? '';

        return [
          {
            id: `${conversationId}:${messageId || 'conversation'}`,
            conversationId,
            conversationTitle,
            messageId,
            role: result.role,
            createdAt: result.createdAt,
            snippet: result.snippet,
            score: result.score,
          },
        ];
      });
    },
    [conversationTitlesById]
  );

  const handleOpenConversationSearchResult = useCallback(
    async (result: WebsiteConversationSearchResult) => {
      const conversationId = result.conversationId.trim();
      if (!conversationId) {
        throw new Error('Conversation ID is required');
      }

      const messageId = result.messageId.trim();
      if (messageId) {
        focusMessageRequestIdRef.current += 1;
        setPendingFocusMessageRequest({
          conversationId,
          messageId,
          requestId: focusMessageRequestIdRef.current,
        });
      } else {
        setPendingFocusMessageRequest(current =>
          current?.conversationId === conversationId ? null : current
        );
      }

      if (selectedConversationId !== conversationId) {
        navigate(chatConversationPath(conversationId));
      }
    },
    [navigate, selectedConversationId]
  );

  const createAndOpenConversation = useCallback(async () => {
    if (createConversationPromiseRef.current) {
      return createConversationPromiseRef.current;
    }
    const createPromise = (async () => {
      setCreatingConversation(true);
      const nextConversation = await conversationModel.createConversation();
      if (!isMountedRef.current) {
        return undefined;
      }
      const nextConversationId = normalizedText(
        nextConversation.conversationId
      );
      if (nextConversationId) {
        navigate(chatConversationPath(nextConversationId));
      }
      return nextConversation;
    })();
    createConversationPromiseRef.current = createPromise;
    try {
      return await createPromise;
    } catch (error) {
      if (isUnauthorizedError(error)) {
        redirectToLogin();
        return undefined;
      }
      throw error;
    } finally {
      if (createConversationPromiseRef.current === createPromise) {
        createConversationPromiseRef.current = null;
      }
      if (isMountedRef.current) {
        setCreatingConversation(false);
      }
    }
  }, [conversationModel, isUnauthorizedError, navigate, redirectToLogin]);

  const handleCreateConversationRequest = useCallback(async () => {
    setThreadError('');
    setThreadErrorStatus(null);

    try {
      const nextConversation = await createAndOpenConversation();
      if (nextConversation && isMountedRef.current) {
        requestComposerFocus();
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }

      const nextError = resolveThreadErrorState(
        error,
        'Failed to create a new task'
      );
      setThreadError(nextError.message);
      setThreadErrorStatus(nextError.status);
    }
  }, [createAndOpenConversation, requestComposerFocus]);

  const handleLogout = useCallback(async () => {
    setThreadError('');
    setThreadErrorStatus(null);

    try {
      await logoutWebsiteUser();
      navigate('/login', { replace: true });
    } catch (error) {
      if (isUnauthorizedError(error)) {
        navigate('/login', { replace: true });
        return;
      }

      const nextError = resolveThreadErrorState(error, 'Failed to log out');
      setThreadError(nextError.message);
      setThreadErrorStatus(nextError.status);
      throw error;
    }
  }, [isUnauthorizedError, navigate]);

  const handleSend = useCallback(
    async (options?: { textOverride?: string }) => {
      if (isSelectedConversationReadOnly) {
        return;
      }
      const nextComposerValue = options?.textOverride ?? composerValue;
      const trimmedText = nextComposerValue.trim();
      const attachmentsSnapshot = composerAttachments;
      const quoteSnapshot = composerQuote;
      if (!trimmedText && attachmentsSnapshot.length === 0) {
        return;
      }
      if (submitInFlightRef.current) {
        return;
      }

      submitInFlightRef.current = true;
      setThreadError('');
      setThreadErrorStatus(null);
      setStreamNotice('');
      let targetConversationId: string | null = selectedConversationId;
      let targetAgentGroupId: string | null = selectedAgentGroupId;
      let targetConversationItem: WebConversationListItem | null =
        selectedConversationListItem;

      try {
        const pendingCreatedConversation = createConversationPromiseRef.current
          ? await createConversationPromiseRef.current
          : null;
        if (pendingCreatedConversation) {
          targetConversationItem = pendingCreatedConversation;
          targetConversationId =
            normalizedText(pendingCreatedConversation.conversationId) ??
            targetConversationId;
          targetAgentGroupId =
            normalizedText(pendingCreatedConversation.agentGroupId) ??
            targetAgentGroupId;
        } else if (!targetConversationId) {
          targetConversationItem = (await createAndOpenConversation()) ?? null;
          targetConversationId =
            normalizedText(targetConversationItem?.conversationId) ?? null;
          targetAgentGroupId =
            normalizedText(targetConversationItem?.agentGroupId) ??
            targetAgentGroupId;
        }

        if (!targetConversationId || !targetAgentGroupId) {
          return;
        }
        if (isBridgeChatVirtualOnboardingSelected) {
          const bridgeConversation =
            await conversationModel.ensureBridgeChatConversation();
          if (!bridgeConversation) {
            throw new Error('Bridge chat is unavailable.');
          }
          targetConversationId =
            normalizedText(bridgeConversation?.conversationId) ??
            targetConversationId;
          targetAgentGroupId =
            normalizedText(bridgeConversation?.agentGroupId) ??
            targetAgentGroupId;
          if (targetConversationId) {
            navigate(chatConversationPath(targetConversationId), {
              replace: true,
            });
          }
        }
        setComposerValue('');
        setComposerQuote(null);
        const targetSessionId =
          conversationModel.runtimeRefForConversation(
            targetConversationId
          )?.sessionId;
        if (attachmentsSnapshot.length > 0 && !targetSessionId) {
          throw new Error(
            `Attachment upload requires backing session metadata for conversation ${targetConversationId}`
          );
        }

        const preparedAttachments: WebAgentMessageContent[] = await Promise.all(
          attachmentsSnapshot.map(attachment =>
            prepareComposerAttachment(attachment, targetSessionId!)
          )
        );

        const { localContent, outboundContent } = buildUserMessageContent({
          text: trimmedText,
          attachments: preparedAttachments,
          quote: quoteSnapshot,
        });

        setFinishState(null);

        const result = await conversationModel.sendConversationMessage(
          targetConversationId,
          {
            content: outboundContent,
            localContent,
            metadata: {
              client: {
                instance_id: clientInstanceId,
                surface: 'web',
              },
              request_context: buildWebRequestContext(),
            },
          }
        );
        if (result.status === 'failed') {
          throw result.error;
        }
        if (result.status === 'read_only') {
          throw new Error('This conversation cannot accept new messages.');
        }
        if (result.status === 'missing_target') {
          throw new Error('No active conversation is available.');
        }

        setComposerValue('');
        clearComposerAttachments(attachmentsSnapshot);
      } catch (error) {
        if (isUnauthorizedError(error)) {
          redirectToLogin();
          return;
        }
        if (isMountedRef.current) {
          setComposerValue(nextComposerValue);
          setComposerQuote(quoteSnapshot);
        }
        const nextError = resolveThreadErrorState(
          error,
          'Failed to send message'
        );
        setThreadError(nextError.message);
        setThreadErrorStatus(nextError.status);
      } finally {
        submitInFlightRef.current = false;
        if (isMountedRef.current) {
          requestComposerFocus();
        }
      }
    },
    [
      composerValue,
      composerAttachments,
      composerQuote,
      clearComposerAttachments,
      clientInstanceId,
      conversationModel,
      createAndOpenConversation,
      isSelectedConversationReadOnly,
      isBridgeChatVirtualOnboardingSelected,
      isUnauthorizedError,
      navigate,
      prepareComposerAttachment,
      redirectToLogin,
      requestComposerFocus,
      selectedAgentGroupId,
      selectedConversationId,
      selectedConversationListItem,
    ]
  );

  const handleSendGeneratedMessage = useCallback(
    async (text: string) => {
      if (isSelectedConversationReadOnly) {
        return;
      }
      const trimmedText = text.trim();
      if (!trimmedText) {
        return;
      }
      if (submitInFlightRef.current) {
        return;
      }

      submitInFlightRef.current = true;
      setThreadError('');
      setThreadErrorStatus(null);
      setStreamNotice('');
      let targetConversationId: string | null = selectedConversationId;
      let targetAgentGroupId: string | null = selectedAgentGroupId;

      try {
        const pendingCreatedConversation = createConversationPromiseRef.current
          ? await createConversationPromiseRef.current
          : null;
        if (pendingCreatedConversation) {
          targetConversationId =
            normalizedText(pendingCreatedConversation.conversationId) ??
            targetConversationId;
          targetAgentGroupId =
            normalizedText(pendingCreatedConversation.agentGroupId) ??
            targetAgentGroupId;
        } else if (!targetConversationId) {
          const targetConversationItem =
            (await createAndOpenConversation()) ?? null;
          targetConversationId =
            normalizedText(targetConversationItem?.conversationId) ?? null;
          targetAgentGroupId =
            normalizedText(targetConversationItem?.agentGroupId) ??
            targetAgentGroupId;
        }

        if (!targetConversationId || !targetAgentGroupId) {
          return;
        }

        const { localContent, outboundContent } = buildUserMessageContent({
          text: trimmedText,
          attachments: [],
          quote: null,
        });

        setFinishState(null);

        const result = await conversationModel.sendConversationMessage(
          targetConversationId,
          {
            content: outboundContent,
            localContent,
            metadata: {
              client: {
                instance_id: clientInstanceId,
                surface: 'web',
              },
              request_context: buildWebRequestContext(),
            },
          }
        );
        if (result.status === 'failed') {
          throw result.error;
        }
        if (result.status === 'read_only') {
          throw new Error('This conversation cannot accept new messages.');
        }
        if (result.status === 'missing_target') {
          throw new Error('No active conversation is available.');
        }
      } catch (error) {
        if (isUnauthorizedError(error)) {
          redirectToLogin();
          return;
        }
        const nextError = resolveThreadErrorState(
          error,
          'Failed to send message'
        );
        setThreadError(nextError.message);
        setThreadErrorStatus(nextError.status);
      } finally {
        submitInFlightRef.current = false;
      }
    },
    [
      clientInstanceId,
      conversationModel,
      createAndOpenConversation,
      isSelectedConversationReadOnly,
      isUnauthorizedError,
      redirectToLogin,
      selectedAgentGroupId,
      selectedConversationId,
    ]
  );

  const handleCancel = useCallback(async () => {
    if (!selectedRuntimeSessionId) {
      return;
    }

    try {
      await cancelAgentSession(selectedRuntimeSessionId);
      setFinishState('cancelled');
    } catch (error) {
      if (isUnauthorizedError(error)) {
        redirectToLogin();
        return;
      }
      const nextError = resolveThreadErrorState(error, 'Failed to cancel run');
      setThreadError(nextError.message);
      setThreadErrorStatus(nextError.status);
    }
  }, [isUnauthorizedError, redirectToLogin, selectedRuntimeSessionId]);

  const handleDeleteConversation = useCallback(
    async (targetConversationId: string) => {
      const targetConversation = conversations.find(
        conversation => conversation.conversationId === targetConversationId
      );
      if (!targetConversation) {
        return;
      }

      const confirmed = window.confirm(
        'Delete this conversation? This cannot be undone.'
      );
      if (!confirmed) {
        return;
      }

      setDeletingConversationId(targetConversationId);
      try {
        const result =
          await conversationModel.deleteConversation(targetConversationId);
        if (!isMountedRef.current) {
          return;
        }

        setThreadError('');
        setThreadErrorStatus(null);

        if (result?.deletedSelectedConversation) {
          navigate(
            result.nextConversationId
              ? chatConversationPath(result.nextConversationId)
              : '/chat',
            { replace: true }
          );
        }
      } catch (error) {
        if (isUnauthorizedError(error)) {
          redirectToLogin();
          return;
        }
        const nextError = resolveThreadErrorState(
          error,
          'Failed to delete conversation'
        );
        setThreadError(nextError.message);
        setThreadErrorStatus(nextError.status);
      } finally {
        if (isMountedRef.current) {
          setDeletingConversationId(current =>
            current === targetConversationId ? null : current
          );
        }
      }
    },
    [
      conversationModel,
      isUnauthorizedError,
      navigate,
      redirectToLogin,
      conversations,
    ]
  );

  const handleRenameConversation = useCallback(
    async (targetConversationId: string) => {
      const targetConversation = conversations.find(
        conversation => conversation.conversationId === targetConversationId
      );
      const targetAgentGroupId =
        normalizedText(targetConversation?.agentGroupId) ??
        selectedAgentGroupId;
      if (!targetConversation || !targetAgentGroupId) {
        return;
      }

      const renamedValue = window.prompt(
        'Rename conversation',
        targetConversation.title.trim()
      );
      if (renamedValue === null) {
        return;
      }

      const nextTitle = renamedValue.trim();
      if (!nextTitle || nextTitle === targetConversation.title.trim()) {
        return;
      }

      setRenamingConversationId(targetConversationId);
      try {
        await conversationModel.renameConversation(
          targetConversationId,
          nextTitle
        );
        if (!isMountedRef.current) {
          return;
        }

        setThreadError('');
        setThreadErrorStatus(null);
      } catch (error) {
        if (isUnauthorizedError(error)) {
          redirectToLogin();
          return;
        }
        const nextError = resolveThreadErrorState(
          error,
          'Failed to rename conversation'
        );
        setThreadError(nextError.message);
        setThreadErrorStatus(nextError.status);
      } finally {
        if (isMountedRef.current) {
          setRenamingConversationId(current =>
            current === targetConversationId ? null : current
          );
        }
      }
    },
    [
      conversationModel,
      isUnauthorizedError,
      redirectToLogin,
      selectedAgentGroupId,
      conversations,
    ]
  );

  const handleShareChat = useCallback(async () => {
    if (
      !selectedShareConversationId ||
      !selectedAgentGroupId ||
      shareState === 'sharing' ||
      shareState === 'revoking' ||
      shareState === 'loading'
    ) {
      return;
    }

    const isStillSelected = () =>
      isMountedRef.current &&
      selectedShareConversationIdRef.current === selectedShareConversationId;

    if (shareURL) {
      await navigator.clipboard.writeText(shareURL);
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 1500);
      return;
    }

    setShareState('loading');
    try {
      const existingShare = await getShareByConversation(
        selectedShareConversationId
      );
      if (!isStillSelected()) {
        return;
      }
      if (existingShare) {
        setShareInfo(existingShare);
        await navigator.clipboard.writeText(existingShare.public_url);
        if (!isStillSelected()) {
          return;
        }
        setIsShareMenuOpen(true);
        setShareState('copied');
        setTimeout(() => setShareState('idle'), 1500);
        return;
      }

      setShareState('sharing');
      const result = await shareChatAPI({
        conversationId: selectedShareConversationId,
        agentGroupId: selectedAgentGroupId,
        title: selectedConversationTitle || '',
      });
      if (!isStillSelected()) {
        return;
      }
      setShareInfo({
        ...result,
        status: 'active',
      });
      await navigator.clipboard.writeText(result.public_url);
      setIsShareMenuOpen(true);
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 1500);
    } catch (error) {
      if (isUnauthorizedError(error)) {
        redirectToLogin();
        return;
      }
      setShareState('idle');
    }
  }, [
    isUnauthorizedError,
    redirectToLogin,
    selectedAgentGroupId,
    selectedShareConversationId,
    selectedConversationTitle,
    shareState,
    shareURL,
  ]);

  const handleUnshareChat = useCallback(async () => {
    if (
      !shareInfo ||
      shareState === 'sharing' ||
      shareState === 'revoking' ||
      shareState === 'loading'
    ) {
      return;
    }

    setShareState('revoking');
    try {
      await revokeSharedChat(shareInfo.id);
      setShareInfo(null);
      setIsShareMenuOpen(false);
      setShareState('idle');
    } catch (error) {
      setShareState('idle');
      const nextError = resolveThreadErrorState(
        error,
        'Failed to unshare conversation'
      );
      setThreadError(nextError.message);
      setThreadErrorStatus(nextError.status);
    }
  }, [shareInfo, shareState]);

  useEffect(() => {
    setShareInfo(null);
    setIsShareMenuOpen(false);
    setShareState('idle');
  }, [selectedShareConversationId]);

  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [selectedConversationId]);

  useEffect(() => {
    setComposerQuote(null);
  }, [selectedConversationId]);

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key !== 'k' && key !== 'f') {
        return;
      }

      event.preventDefault();
      openSearchPanel();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openSearchPanel]);

  return (
    <MinimapOptionsProvider value={minimapOptions}>
      <div
        ref={pageRef}
        className="h-[100dvh] overflow-hidden bg-background text-foreground"
      >
        <div className="flex h-full w-full overflow-hidden">
          <div
            className={cn(
              'hidden h-full shrink-0 overflow-hidden transition-[width,opacity] duration-300 ease-out lg:flex',
              isDesktopSidebarOpen
                ? 'w-[280px] opacity-100 xl:w-[292px]'
                : 'w-0 opacity-0 pointer-events-none'
            )}
          >
            <ChatSidebar
              className={cn(
                'flex h-full w-[280px] shrink-0 flex-col border-r border-border/70 transition-transform duration-300 ease-out xl:w-[292px]',
                isDesktopSidebarOpen ? 'translate-x-0' : '-translate-x-6'
              )}
              conversations={conversations}
              activeConversationId={selectedConversationId ?? undefined}
              loading={loadingConversations}
              hasMoreConversations={hasMoreConversationList}
              loadingMoreConversations={loadingMoreConversations}
              autoLoadMoreThreshold={
                CONVERSATION_SIDEBAR_AUTO_LOAD_MORE_THRESHOLD_PX
              }
              enableAutoLoadMore={isDesktopSidebarOpen}
              isSidebarOpen={isDesktopSidebarOpen}
              isSearchOpen={isSearchOpen}
              currentUser={currentUser}
              credits={currentUserCredits}
              loadingCredits={loadingCredits}
              onSelect={nextConversationId => {
                requestComposerFocus();
                navigate(chatConversationPath(nextConversationId));
              }}
              creatingConversation={creatingConversation}
              onCreate={() => {
                void handleCreateConversationRequest();
              }}
              onSearchOpen={openSearchPanel}
              onToggleSidebar={toggleDesktopSidebar}
              onLoadMoreConversations={() => {
                void loadMoreConversations();
              }}
              onDelete={nextConversationId => {
                void handleDeleteConversation(nextConversationId);
              }}
              onRename={nextConversationId => {
                void handleRenameConversation(nextConversationId);
              }}
              onLogout={handleLogout}
              onOpenBilling={() => navigate('/settings/billing/credits')}
              onOpenRedeem={() => navigate('/settings/billing/redeem')}
              onOpenBook={() => navigate('/book')}
              onOpenComputers={() => navigate('/settings/computers')}
              onOpenIntegrations={() =>
                navigate('/settings/integrations/discord')
              }
              onDownloadClient={handleDownloadClient}
              deletingConversationId={deletingConversationId}
              renamingConversationId={renamingConversationId}
            />
          </div>

          <main className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background">
            <div
              className={cn(
                'shrink-0 px-4 py-2.5 md:px-4',
                'border-b transition-colors duration-300 ease-out',
                isVFSOpen ? 'border-border/70' : 'border-transparent'
              )}
            >
              <div className="flex h-8 items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="rounded-md text-muted-foreground shadow-none hover:bg-muted/60 hover:text-foreground lg:hidden"
                    onClick={openMobileSidebar}
                    aria-label="Open chats"
                  >
                    <PanelLeft {...chatLucideIconProps} className="size-4" />
                  </Button>

                  <div
                    className={cn(
                      'hidden lg:flex shrink-0 items-center justify-center overflow-hidden transition-[width,opacity,margin,transform] duration-300 ease-out',
                      isDesktopSidebarOpen
                        ? 'mr-0 w-0 -translate-x-2 opacity-0 pointer-events-none'
                        : 'mr-1.5 w-8 translate-x-0 opacity-100'
                    )}
                  >
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="rounded-md text-muted-foreground shadow-none hover:bg-muted/60 hover:text-foreground"
                      onClick={toggleDesktopSidebar}
                      aria-label="Expand chats"
                    >
                      <PanelLeft {...chatLucideIconProps} className="size-4" />
                    </Button>
                  </div>

                  {!isBridgeChatOnboardingSelected ? (
                    <div className="chat-type-body-sm-strong truncate text-foreground">
                      {selectedConversationTitle}
                    </div>
                  ) : null}

                  {selectedConversationId && !isBridgeChatOnboardingSelected ? (
                    <span className="chat-type-caption inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/70 bg-background px-2 py-0.5 text-muted-foreground">
                      <span
                        className={cn(
                          'size-1.5 rounded-full',
                          finishState === 'waiting'
                            ? 'bg-amber-500'
                            : isConversationProcessing
                              ? 'bg-neutral-400'
                              : 'bg-emerald-500'
                        )}
                      />
                      {finishState === 'waiting'
                        ? 'Waiting'
                        : isConversationProcessing
                          ? 'Running'
                          : 'Ready'}
                    </span>
                  ) : null}
                </div>

                <div className="flex items-center gap-1.5">
                  {!isBridgeChatSelected ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className={cn(
                        'rounded-md text-muted-foreground shadow-none hover:bg-muted/60 hover:text-foreground',
                        isSearchOpen && 'bg-muted/60 text-foreground'
                      )}
                      onClick={openSearchPanel}
                      aria-label="Search conversations"
                    >
                      <Search {...chatLucideIconProps} className="size-4" />
                    </Button>
                  ) : null}

                  {showSelectedConversationControls &&
                  selectedRuntimeSessionId &&
                  messages.length > 0 ? (
                    shareInfo ? (
                      <DropdownMenu
                        open={isShareMenuOpen}
                        onOpenChange={setIsShareMenuOpen}
                      >
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="flex size-8 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors duration-200 hover:bg-muted/60 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/30 disabled:pointer-events-none disabled:opacity-50"
                            disabled={
                              shareState === 'sharing' ||
                              shareState === 'revoking' ||
                              shareState === 'loading'
                            }
                            aria-label="Share options"
                          >
                            {shareState === 'sharing' ||
                            shareState === 'revoking' ||
                            shareState === 'loading' ? (
                              <Loader2
                                {...chatLucideIconProps}
                                className="size-4 animate-spin"
                              />
                            ) : (
                              <Share2
                                {...chatLucideIconProps}
                                className="size-4"
                              />
                            )}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => {
                              void handleShareChat();
                            }}
                          >
                            Copy share link
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => {
                              void handleUnshareChat();
                            }}
                          >
                            Cancel share
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="rounded-md text-muted-foreground shadow-none hover:bg-muted/60 hover:text-foreground"
                        disabled={
                          shareState === 'sharing' ||
                          shareState === 'revoking' ||
                          shareState === 'loading'
                        }
                        onClick={() => {
                          void handleShareChat();
                        }}
                        aria-label="Copy share link"
                      >
                        {shareState === 'sharing' ||
                        shareState === 'revoking' ||
                        shareState === 'loading' ? (
                          <Loader2
                            {...chatLucideIconProps}
                            className="size-4 animate-spin"
                          />
                        ) : (
                          <Share2 {...chatLucideIconProps} className="size-4" />
                        )}
                      </Button>
                    )
                  ) : null}

                  {showSelectedConversationControls ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className={cn(
                        'rounded-md text-muted-foreground shadow-none hover:bg-muted/60 hover:text-foreground',
                        isVFSOpen && 'bg-muted/60 text-foreground'
                      )}
                      onClick={toggleVFS}
                      aria-label={isVFSOpen ? 'Hide files' : 'Show files'}
                    >
                      <FolderOpen {...chatLucideIconProps} className="size-4" />
                    </Button>
                  ) : null}

                  {showSelectedConversationControls &&
                  selectedConversationId ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="rounded-md text-muted-foreground shadow-none hover:bg-muted/60 hover:text-foreground lg:hidden"
                      onClick={() => {
                        void handleDeleteConversation(selectedConversationId);
                      }}
                      aria-label="Delete chat"
                      disabled={
                        deletingConversationId === selectedConversationId
                      }
                    >
                      {deletingConversationId === selectedConversationId ? (
                        <Loader2
                          {...chatLucideIconProps}
                          className="size-4 animate-spin"
                        />
                      ) : (
                        <Trash2 {...chatLucideIconProps} className="size-4" />
                      )}
                    </Button>
                  ) : null}

                  {!isBridgeChatSelected ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="rounded-md text-muted-foreground shadow-none hover:bg-muted/60 hover:text-foreground"
                      onClick={() => {
                        void handleCreateConversationRequest();
                      }}
                      aria-label="New Chat"
                      disabled={creatingConversation}
                    >
                      {creatingConversation ? (
                        <Loader2
                          {...chatLucideIconProps}
                          className="size-4 animate-spin"
                        />
                      ) : (
                        <SquarePen
                          {...chatLucideIconProps}
                          className="size-4"
                        />
                      )}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 bg-background">
              <div
                ref={chatSurfaceRef}
                className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-background"
              >
                {isBridgeChatOnboardingSelected &&
                bridgeComposerConnectorPath ? (
                  <svg
                    className="pointer-events-none absolute inset-0 z-[1] hidden text-foreground/45 md:block"
                    viewBox={`0 0 ${bridgeComposerConnectorPath.width} ${bridgeComposerConnectorPath.height}`}
                    preserveAspectRatio="none"
                    aria-hidden="true"
                    data-bridge-composer-connector="true"
                  >
                    <path
                      d={bridgeComposerConnectorPath.path}
                      stroke="currentColor"
                      strokeWidth="1.35"
                      strokeLinecap="round"
                      fill="none"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                ) : null}

                {showConversationLoading ? (
                  <ChatLoadingState />
                ) : isBridgeChatOnboardingSelected ? (
                  <BridgeChatOnboardingState />
                ) : !selectedConversationId || !hasRenderableConversation ? (
                  <ChatWelcomeState user={currentUser} />
                ) : (
                  <ChatScrollViewport
                    contentBoxId={WEBSITE_CHAT_CONTENT_BOX_ID}
                    scrollContainerRef={scrollContainerRef}
                    enableAutoScroll={isConversationProcessing}
                    showScrollAnchor={hasRenderableConversation}
                    bottomInset={chatBottomInset}
                  >
                    <InteractiveLoginCompleterProvider
                      complete={async loginId => {
                        if (!selectedRuntimeSessionId) {
                          throw new Error('No active session');
                        }
                        return completeInteractiveLogin(
                          selectedRuntimeSessionId,
                          loginId
                        );
                      }}
                    >
                      <ConversationThread
                        messages={messages}
                        assistantStateSequence={assistantStateSequence}
                        isStreaming={isConversationProcessing}
                        className="gap-3.5"
                        showMessageIdentity={false}
                        onSendMessage={
                          isSelectedConversationReadOnly
                            ? undefined
                            : handleSendGeneratedMessage
                        }
                        onApproveLocationRequest={
                          approveLocationCapabilityRequest
                        }
                        onConfirmOAuthAuthorizationRequest={
                          confirmOAuthAuthorizationCapabilityRequest
                        }
                        onCompleteInteractiveLoginRequest={
                          completeInteractiveLoginCapabilityRequest
                        }
                        onDecideHostAccessRequest={
                          decideHostAccessCapabilityRequest
                        }
                        onDecideComputerUseStartRequest={
                          decideComputerUseStartCapabilityRequest
                        }
                        onAttachmentImageLayout={(
                          messageId,
                          attachmentKey,
                          imageSize
                        ) => {
                          selectedMessageThread?.recordAttachmentImageLayout(
                            messageId,
                            attachmentKey,
                            imageSize
                          );
                        }}
                      />
                      {displayedParticipantActivities.map(item => (
                        <AssistantActivityLine
                          key={item.key}
                          activity={item.activity}
                          agentName={participantActivityAgentName(item)}
                          resetKey={`${selectedConversationId ?? 'none'}:${item.key}`}
                          className="mx-auto w-full max-w-[940px]"
                        />
                      ))}
                    </InteractiveLoginCompleterProvider>
                  </ChatScrollViewport>
                )}

                {!isSelectedConversationReadOnly ? (
                  <QuoteSelectionBubble
                    containerRef={scrollContainerRef}
                    onAskQuote={selection => {
                      setComposerQuote({
                        text: selection.text,
                        quoteRef: selection.quoteRef,
                      });
                      window.setTimeout(() => {
                        document
                          .querySelector<HTMLTextAreaElement>(
                            '[data-chat-composer-input="true"]'
                          )
                          ?.focus();
                      }, 0);
                    }}
                  />
                ) : null}

                {!isSelectedConversationReadOnly ||
                streamNotice ||
                threadError ? (
                  <div className="bg-background px-4 pb-4 md:px-5 md:pb-5">
                    <div className="mx-auto w-full max-w-[940px]">
                      {streamNotice ? (
                        <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200/70 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                          <Loader2
                            {...chatLucideIconProps}
                            className="mt-0.5 size-4 shrink-0 animate-spin"
                          />
                          <span>{streamNotice}</span>
                        </div>
                      ) : null}

                      {showPaymentRequiredBanner ? (
                        <PaymentRequiredBanner
                          message={threadError}
                          credits={currentUserCredits}
                          loadingCredits={loadingCredits}
                          onOpenBilling={() =>
                            navigate('/settings/billing/credits')
                          }
                          onOpenRedeem={() =>
                            navigate('/settings/billing/redeem')
                          }
                          onRefreshCredits={() => {
                            void refreshCurrentUserCredits();
                          }}
                        />
                      ) : null}

                      {threadError && !showPaymentRequiredBanner ? (
                        <div className="mb-3 flex items-start gap-2 rounded-xl border border-rose-200/70 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                          <AlertTriangle
                            {...chatLucideIconProps}
                            className="mt-0.5 size-4 shrink-0"
                          />
                          <span>{threadError}</span>
                        </div>
                      ) : null}

                      {!isSelectedConversationReadOnly ? (
                        <div className="relative">
                          <div
                            className="relative z-10"
                            data-chat-composer-anchor="true"
                          >
                            <ChatComposer
                              value={composerValue}
                              disabled={creatingConversation}
                              isSubmitting={modelIsSubmittingMessage}
                              canCancel={isConversationProcessing}
                              canSubmit={canSubmitComposer}
                              draftQuote={composerQuote}
                              attachments={composerAttachments}
                              focusRequestKey={composerFocusRequestKey}
                              onChange={setComposerValue}
                              onSubmit={handleSend}
                              onCancel={() => {
                                void handleCancel();
                              }}
                              onRemoveQuote={() => setComposerQuote(null)}
                              onActivateQuote={() => {
                                if (composerQuote) {
                                  focusQuote(composerQuote.quoteRef);
                                }
                              }}
                              onPickFiles={handlePickComposerFiles}
                              onRemoveAttachment={
                                handleRemoveComposerAttachment
                              }
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <div
                className={cn(
                  'hidden h-full shrink-0 overflow-hidden transition-[width,opacity] duration-300 ease-out xl:flex',
                  isVFSOpen
                    ? 'w-[340px] opacity-100'
                    : 'w-0 opacity-0 pointer-events-none'
                )}
              >
                <aside
                  className={cn(
                    'h-full w-[340px] shrink-0 transition-transform duration-300 ease-out',
                    isVFSOpen ? 'translate-x-0' : 'translate-x-6'
                  )}
                >
                  <VFSBrowser
                    currentPath={vfsPath}
                    entries={vfsEntries}
                    isLoading={isLoadingVFS}
                    error={vfsError}
                    isUploading={isUploadingVFS}
                    downloadingPath={downloadingVFSPath}
                    onNavigate={path => {
                      void loadVFS(path);
                    }}
                    onRefresh={() => {
                      void loadVFS(vfsPath);
                    }}
                    onUpload={async files => {
                      await handleUploadVFSFiles(files);
                    }}
                    onPreview={path => {
                      handlePreviewVFSFile(path);
                    }}
                    onDownload={async path => {
                      await handleDownloadVFSFile(path);
                    }}
                    onClose={closeVFS}
                  />
                </aside>
              </div>
            </div>
          </main>
        </div>

        <div
          className={cn(
            'fixed inset-0 z-50 bg-neutral-950/35 transition-opacity duration-300 lg:hidden',
            isMobileSidebarOpen
              ? 'opacity-100'
              : 'pointer-events-none opacity-0'
          )}
          onClick={closeMobileSidebar}
        >
          <div
            className={cn(
              'absolute inset-y-0 left-0 w-full max-w-[292px] overflow-hidden bg-background shadow-2xl transition-transform duration-300 ease-out',
              isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-6'
            )}
            onClick={event => {
              event.stopPropagation();
            }}
          >
            <ChatSidebar
              className="flex h-full w-full flex-col"
              conversations={conversations}
              activeConversationId={selectedConversationId ?? undefined}
              loading={loadingConversations}
              hasMoreConversations={hasMoreConversationList}
              loadingMoreConversations={loadingMoreConversations}
              autoLoadMoreThreshold={
                CONVERSATION_SIDEBAR_AUTO_LOAD_MORE_THRESHOLD_PX
              }
              enableAutoLoadMore={isMobileSidebarOpen}
              isSidebarOpen
              isSearchOpen={isSearchOpen}
              currentUser={currentUser}
              credits={currentUserCredits}
              loadingCredits={loadingCredits}
              onSelect={nextConversationId => {
                closeMobileSidebar();
                requestComposerFocus();
                navigate(chatConversationPath(nextConversationId));
              }}
              creatingConversation={creatingConversation}
              onCreate={() => {
                closeMobileSidebar();
                void handleCreateConversationRequest();
              }}
              onSearchOpen={() => {
                closeMobileSidebar();
                openSearchPanel();
              }}
              onToggleSidebar={closeMobileSidebar}
              onLoadMoreConversations={() => {
                void loadMoreConversations();
              }}
              onDelete={nextConversationId => {
                void handleDeleteConversation(nextConversationId);
              }}
              onRename={nextConversationId => {
                void handleRenameConversation(nextConversationId);
              }}
              onLogout={handleLogout}
              onOpenBilling={() => {
                closeMobileSidebar();
                navigate('/settings/billing/credits');
              }}
              onOpenRedeem={() => {
                closeMobileSidebar();
                navigate('/settings/billing/redeem');
              }}
              onOpenBook={() => {
                closeMobileSidebar();
                navigate('/book');
              }}
              onOpenComputers={() => {
                closeMobileSidebar();
                navigate('/settings/computers');
              }}
              onOpenIntegrations={() => {
                closeMobileSidebar();
                navigate('/settings/integrations/discord');
              }}
              onDownloadClient={handleDownloadClient}
              deletingConversationId={deletingConversationId}
              renamingConversationId={renamingConversationId}
            />
          </div>
        </div>

        <div
          className={cn(
            'fixed inset-0 z-40 bg-neutral-950/35 transition-opacity duration-300 xl:hidden',
            isVFSOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          )}
          onClick={closeVFS}
        >
          <div
            className={cn(
              'absolute inset-y-0 right-0 w-full max-w-sm bg-background shadow-2xl transition-transform duration-300 ease-out',
              isVFSOpen ? 'translate-x-0' : 'translate-x-full'
            )}
            onClick={event => {
              event.stopPropagation();
            }}
          >
            <VFSBrowser
              currentPath={vfsPath}
              entries={vfsEntries}
              isLoading={isLoadingVFS}
              error={vfsError}
              isUploading={isUploadingVFS}
              downloadingPath={downloadingVFSPath}
              onNavigate={path => {
                void loadVFS(path);
              }}
              onRefresh={() => {
                void loadVFS(vfsPath);
              }}
              onUpload={async files => {
                await handleUploadVFSFiles(files);
              }}
              onPreview={path => {
                handlePreviewVFSFile(path);
              }}
              onDownload={async path => {
                await handleDownloadVFSFile(path);
              }}
              onClose={closeVFS}
            />
          </div>
        </div>

        <ConversationSearchPanel
          open={isSearchOpen}
          requestToken={searchPanelRequestVersion}
          onClose={closeSearchPanel}
          onSearch={handleConversationSearch}
          onOpenResult={handleOpenConversationSearchResult}
        />
        <ChatFilePreviewModal
          preview={filePreview}
          onClose={() => setFilePreview(null)}
          onDownload={async preview => {
            if (preview.filePath) {
              await handleDownloadVFSFile(preview.filePath);
            }
          }}
        />
      </div>
    </MinimapOptionsProvider>
  );
}
